import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactGA from 'react-ga4';
import Box from '@mui/material/Box';

import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import ShowDetailsScreen from './screens/ShowDetailsScreen';
import PeopleScreen from './screens/PeopleScreen';
import ReminderScreen from './screens/ReminderScreen';
import WatchlistScreen from './screens/WatchlistScreen';
import PrivacyPolicyScreen from './screens/PrivacyPolicyScreen';
import ProfileScreen from './screens/ProfileScreen';
import SearchedScreen from './screens/SearchedScreen';
import Footer from './components/Footer';
import Header from './components/Header';
import OfflineBanner from './components/OfflineBanner';
import { useColorMode } from './theme/ThemeContext';
import { isNativeApp } from './utils/platform';
import { installExternalLinkHandler } from './utils/openExternal';
import { App as CapApp } from '@capacitor/app';

// CRA sets NODE_ENV to 'production' for `npm run build` and 'development' for
// `npm start` — so GA fires for the deployed build but not local dev. (The old
// guard used REACT_APP_ENV, which was never defined, so GA never initialized.)
const gaEnabled = process.env.NODE_ENV === 'production';

if (gaEnabled) {
  ReactGA.initialize('G-L4RWP16VTP');
}

const RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (gaEnabled) {
      ReactGA.send({
        hitType: 'pageview',
        page: location.pathname + location.search,
      });
    }
  }, [location]);

  return null;
};

const App = () => {
  const [isWebView, setIsWebView] = useState(false);
  const { mode } = useColorMode();

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const isWebViewParam = query.get('wv');
    setIsWebView(isWebViewParam === 'true/');
  }, []);

  // Android hardware/gesture back button: navigate back through the SPA history
  // instead of exiting the app. Only exit when there's nothing left to go back to
  // (i.e. on the initial screen). Web/browser is unaffected.
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    let sub;
    CapApp.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        CapApp.exitApp();
      }
    }).then((handle) => {
      sub = handle;
    });
    return () => {
      if (sub) sub.remove();
    };
  }, []);

  // Android app: links that leave the site open through the system
  // "Open with" chooser so the user picks the browser/app.
  useEffect(() => installExternalLinkHandler(), []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Router>
        <Header />
        <RouteTracker />
        <Box component="main" sx={{ flex: 1 }}>
          <Routes>
            <Route path="/" exact element={<HomeScreen />} />
            <Route path="/login" element={<LoginScreen />} />
            <Route path="/details/?" element={<ShowDetailsScreen />} />
            <Route path="/person/?" element={<PeopleScreen />} />
            <Route path="/reminder" element={<ReminderScreen />} />
            <Route path="/watchlist" element={<WatchlistScreen />} />
            <Route path="/policy" element={<PrivacyPolicyScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/search" element={<SearchedScreen />} />
          </Routes>
        </Box>
        {!isWebView && !isNativeApp() && <Footer />}
        <OfflineBanner />
        <ToastContainer
          theme={mode === 'dark' ? 'dark' : 'light'}
          position="bottom-center"
        />
      </Router>
    </Box>
  );
};

export default App;
